import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./LoanRequestPage.module.scss";
import { ReactComponent as BackIcon } from "../../../assets/icons/arrow_forward.svg";
import LoanRequestCards from "./components/LoanRequestCards/LoanRequestCards";
import GridSearchBar from "../../../common/components/grid-search-bar/GridSearchBar";
import TabSelector from "../../../common/components/tab-selector/TabSelector";
import PendingRequestTab from "./components/LoanRequestTabs/PendingRequestTab";
import ApprovedRequestTab from "./components/LoanRequestTabs/ApprovedRequestTab";
import DeclinedRequestTab from "./components/LoanRequestTabs/DeclinedRequestTab";
import LoanRequestDetails from "./components/LoanRequestDetails/LoanRequestDetails";
import { OrderStatus, OrderType } from "../../../model/orders/orders";
import CommonPagination from "../../../common/components/pagination/CommonPagination";
import { toast } from "react-toastify";
import ordersService from "../../../httpService/OrdersService";

export type CountResponse = {
	totalCount: number;
	pendingCount: number;
	declinedCount: number;
	approvedCount: number;
};

type Props = {};

const LoanRequestPage = memo((props: Props) => {
	const navigate = useNavigate();

	const [searchValue, setSearchValue] = useState<string>("");
	const [debounceSearchValue, setDebounceSearchValue] = useState<string>("");
	const [orderType, setOrderType] = useState<OrderStatus>("Pending");

	const pageSize = 6;
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [loading, setLoading] = useState(false);

	const [selectedTab, setSelectedTab] = useState(1);
	const [data, setData] = useState<OrderType[]>();
	const [orderDetails, setOrderDetails] = useState<OrderType | null>(null);
	const [dataCount, setDataCount] = useState<CountResponse>({
		totalCount: 0,
		pendingCount: 0,
		declinedCount: 0,
		approvedCount: 0,
	});

	const fetchDataCount = useCallback(async () => {
		try {
			const response = await ordersService.get("count");
			setDataCount(response.data);
		} catch (error: any) {
			toast.error(`${error.name} - ${error.message}`);
			console.error("There was an error!", error);
		}
	}, [ordersService]);

	const tabs = [
		{ id: 1, title: "Pending Request", onClick: () => changeTab(1) },
		{ id: 2, title: "Approved Request", onClick: () => changeTab(2) },
		{ id: 3, title: "Declined Request", onClick: () => changeTab(3) },
	];

	const changeTab = (tabId: number) => {
		setSelectedTab(tabId);
		setCurrentPage(1);
	};

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			const params = {
				page: currentPage,
				pageSize: pageSize,
				status: orderType,
				paymentMethod: "Installments",
				currentPage,
				search: debounceSearchValue ?? "",
			};
			const response = await ordersService.get("", { params });
			const data = response.data;
			setTotalPages(data.totalPages);
			setData(data.items);
		} catch (error: any) {
			toast.error(`${error.name} - ${error.message}`);
			console.error("There was an error!", error);
		}
		setLoading(false);
	}, [debounceSearchValue, orderType, currentPage, ordersService]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebounceSearchValue(searchValue);
		}, 500);
		return () => clearTimeout(timer);
	}, [searchValue]);

	const refetchData = useCallback(() => {
		fetchData();
		fetchDataCount();
	}, [fetchData, fetchDataCount]);	

	useEffect(() => {
		refetchData();
	}, [refetchData]);

	useEffect(() => {
		switch (selectedTab) {
			case 1:
				setOrderType("Pending");
				break;
			case 2:
				setOrderType("Approved");
				break;
			case 3:
				setOrderType("Declined");
				break;
			default:
				setOrderType("Pending");
		}
	}, [selectedTab]);

	const renderTabContent = () => {
		switch (selectedTab) {
			case 1:
				return <PendingRequestTab setOrderDetails={setOrderDetails} data={data ?? []} />;
			case 2:
				return <ApprovedRequestTab setOrderDetails={setOrderDetails} data={data ?? []} />;
			case 3:
				return <DeclinedRequestTab setOrderDetails={setOrderDetails} data={data ?? []} />;
			default:
				return <PendingRequestTab setOrderDetails={setOrderDetails} data={data ?? []} />;
		}
	};

	if (!!orderDetails) {
		return <LoanRequestDetails orderDetails={orderDetails} setOrderDetails={setOrderDetails} refetchData={refetchData} />;
	}

	return (
		<section className={styles.loanRequestPage}>
			<div className={styles.pageTitle}>
				<div className={styles.icon} onClick={() => navigate(-1)}>
					<BackIcon />
				</div>
				Loan Request
			</div>

			<div className={styles.contentWrapper}>
				<LoanRequestCards data={dataCount} />

				<div className={styles.filtersWrapper}>
					<div className={styles.filters}>
						<GridSearchBar placeholder={"Seach by name"} width="200px" onChangeHandler={value => setSearchValue(value)} />
					</div>
				</div>

				<div className={styles.tabSelectorWrapper}>
					<div className={styles.tabContainer}>
						<TabSelector tabs={tabs} selectedTab={selectedTab} theme="yellow" />
					</div>
					{renderTabContent()}
					<div className={styles.paginationContainer}>
						<CommonPagination
							currentPage={currentPage}
							isLoading={loading}
							numberOfPages={totalPages}
							onPageChange={(page: number) => {
								setCurrentPage(page);
							}}
						/>
					</div>
				</div>
			</div>
		</section>
	);
});

export default LoanRequestPage;
